import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { DetailView } from '../DetailView/DetailView';
import { Modules } from '../../common/constants/sidebar';
import './ExpiringView.css';

const COLORS = {
  'Billable': '#0088FE',
  'Non-Billable': '#00C49F', 
  'Shadow': '#FFBB28',
  'billable': '#0088FE',
  'non-billable': '#00C49F',
  'shadow': '#FFBB28'
};

const EXPIRY_COLORS = {
  'next10days': '#dc3545',
  'next20days': '#fd7e14',
  'next30days': '#ffc107',
  'expired': '#6f42c1'
};

const ExpiringView = ({ expiringResources, allProjectManagementData }) => {
  const [expiryFilter, setExpiryFilter] = useState('next10days');
  const [viewMode, setViewMode] = useState('tabular'); // 'tabular' or 'chart'
  
  // States for project allocation modal (same as BenchView)
  const [viewProjectDetails, setViewProjectDetails] = useState(false);
  const [selectedEmployeeIdForDetail, setSelectedEmployeeIdForDetail] = useState('');
  
  console.log("ExpiringView received expiringResources:", {
    expiringResources,
    type: typeof expiringResources,
    keys: expiringResources ? Object.keys(expiringResources) : 'null',
    expired: expiringResources?.expired?.length || 0,
    next10days: expiringResources?.next10days?.length || 0,
    next20days: expiringResources?.next20days?.length || 0,
    next30days: expiringResources?.next30days?.length || 0,
    allProjectManagementData: allProjectManagementData?.data?.length || 0
  });

  // Helper function to find employee ID for project name lookup
  const findEmployeeId = (resource) => {
    const idFields = ['zohoEmployeeId', 'employeeId', 'Employee ID', 'EmployeeId', 'empId', 'emp_id', 'employee_id', 'employeeGuid'];
    for (const field of idFields) {
      if (resource[field] !== undefined && resource[field] !== null && resource[field] !== '') {
        // Handle array format (new API structure) - take first element
        const value = Array.isArray(resource[field]) ? resource[field][0] : resource[field];
        return String(value).trim();
      }
    }
    return null;
  };

  const findProjectNameByEmployeeId = (employeeId, fallbackProject = null) => {
    if (!allProjectManagementData?.data || !employeeId) {
      return fallbackProject || 'Unknown Project';
    }

    // Find the project allocation for this employee ID
    const projectAllocation = allProjectManagementData.data.find(allocation => {
      const allocEmployeeId = findEmployeeId(allocation);
      return allocEmployeeId === employeeId;
    });

    if (projectAllocation) {
      // Extract project name from the allocation data
      const projectFields = ['projectName', 'Project Name', 'ProjectName', 'Project', 'project', 'Project_Name'];
      for (const field of projectFields) {
        if (projectAllocation[field]) {
          return String(projectAllocation[field]).trim();
        }
      }
    }

    return fallbackProject || 'Unknown Project'; // Fallback to original project field
  };


  const formatDate = (date) => {
    if (!date || !(date instanceof Date)) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getExpiryData = () => {
    if (!expiringResources || typeof expiringResources !== 'object') {
      console.warn('ExpiringView: expiringResources is not a valid object:', expiringResources);
      return [];
    }
    
    const data = expiringResources[expiryFilter] || [];
    console.log(`Getting data for filter '${expiryFilter}':`, data.length, 'items');
    return data;
  };

  // Handle employee click for project detail view (same logic as BenchView)
  const handleEmployeeClick = (resource) => {
    console.log('Employee clicked in ExpiringView:', resource);
    console.log('Employee ID (current):', resource.employeeId);
    console.log('Employee GUID ID:', resource.employeeGuid);
    
    // Use the GUID employee ID if available, fallback to regular employeeId
    const employeeIdToUse = resource.employeeGuid || resource.employeeId;
    console.log('Using employee ID:', employeeIdToUse);
    
    if (employeeIdToUse && employeeIdToUse !== 'N/A') {
      setSelectedEmployeeIdForDetail(employeeIdToUse);
      setViewProjectDetails(true);
    } else {
      console.warn('No valid employee ID found for:', resource);
    }
  };

  const handleCloseProjectDetailModal = () => {
    setViewProjectDetails(false);
    setSelectedEmployeeIdForDetail('');
  };

  return (
    <div className="expiring-view">
      {/* View Mode Toggle */}
      <div className="view-toggle-section">
        <div className="view-toggle-container">
          <span className="view-label">📋 Tabular View</span>
          <div className="toggle-switch">
            <input
              type="checkbox"
              id="viewToggle"
              className="toggle-input"
              checked={viewMode === 'chart'}
              onChange={(e) => setViewMode(e.target.checked ? 'chart' : 'tabular')}
            />
            <label htmlFor="viewToggle" className="toggle-label">
              <span className="toggle-slider"></span>
            </label>
          </div>
          <span className="view-label">📊 Chart View</span>
        </div>
      </div>

      {/* Expiring Allocations Controls */}
      <div className="expiry-controls">
        <h3>📅 Allocation Expiry Timeframe</h3>
        <div className="expiry-filters">
          <button 
            className={`expiry-btn next10 ${expiryFilter === 'next10days' ? 'active' : ''}`}
            onClick={() => setExpiryFilter('next10days')}
          >
            🔴 Next 10 Days ({expiringResources?.next10days?.length || 0})
          </button>
          <button 
            className={`expiry-btn next20 ${expiryFilter === 'next20days' ? 'active' : ''}`}
            onClick={() => setExpiryFilter('next20days')}
          >
            🟠 Next 20 Days ({expiringResources?.next20days?.length || 0})
          </button>
          <button 
            className={`expiry-btn next30 ${expiryFilter === 'next30days' ? 'active' : ''}`}
            onClick={() => setExpiryFilter('next30days')}
          >
            🟡 Next 30 Days ({expiringResources?.next30days?.length || 0})
          </button>
        </div>
      </div>

      {/* Conditional Rendering Based on View Mode */}
      {viewMode === 'tabular' ? (
        /* Tabular View - Only Table Grid */
        <div className="chart-section full-width">
          <h2>⚠️ Resources with Expiring Allocations - {expiryFilter.replace('next', 'Next ').replace('days', ' Days')}</h2>
          {getExpiryData().length > 0 ? (
            <div className="expiring-resources-table">
              <table>
                <thead>
                  <tr>
                    <th>Resource Name</th>
                    <th>Project</th>
                    <th>Bill Status</th>
                    <th>Allocation %</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Days Until Expiry</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {getExpiryData().map((resource, index) => {
                    const projectName = findProjectNameByEmployeeId(findEmployeeId(resource), resource.project);
                    return (
                    <tr key={index} className={`expiry-row ${resource.daysUntilExpiry < 0 ? 'expired' : resource.daysUntilExpiry <= 10 ? 'urgent' : resource.daysUntilExpiry <= 20 ? 'warning' : 'caution'}`}>
                      <td 
                        className="resource-name clickable-employee-name" 
                        onClick={() => handleEmployeeClick(resource)}
                        style={{ 
                          cursor: 'pointer', 
                          color: '#1773bc',
                          fontWeight: '600'
                        }}
                        title={`Click to view project allocation details (Employee ID: ${resource.employeeId || 'N/A'}, GUID: ${resource.employeeGuid || 'N/A'})`}
                      >
                        {resource.name} {resource.employeeId && <span style={{fontSize: '0.8em', color: '#666'}}>({resource.employeeId})</span>}
                      </td>
                      <td className="project-name">{projectName}</td>
                      <td>
                        <span 
                          className="status-badge" 
                          style={{backgroundColor: COLORS[resource.billStatus] || '#ccc'}}
                        >
                          {resource.billStatus}
                        </span>
                      </td>
                      <td className="allocation">
                        {resource.allocation !== null && 
                         resource.allocation !== undefined && 
                         !isNaN(resource.allocation) && 
                         isFinite(resource.allocation) 
                          ? (resource.allocation * 100).toFixed(1) 
                          : '0.0'}%
                      </td>
                      <td>{formatDate(resource.startDate)}</td>
                      <td className="end-date">{formatDate(resource.endDate)}</td>
                      <td className="days-until-expiry">
                        {resource.daysUntilExpiry < 0 
                          ? `${Math.abs(resource.daysUntilExpiry)} days ago` 
                          : `${resource.daysUntilExpiry} days`}
                      </td>
                      <td>
                        <span className={`expiry-status ${resource.daysUntilExpiry < 0 ? 'expired' : resource.daysUntilExpiry <= 10 ? 'urgent' : resource.daysUntilExpiry <= 20 ? 'warning' : 'caution'}`}>
                          {resource.daysUntilExpiry < 0 ? 'EXPIRED' : 
                           resource.daysUntilExpiry <= 10 ? 'URGENT' :
                           resource.daysUntilExpiry <= 20 ? 'WARNING' : 'CAUTION'}
                        </span>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-expiring-resources">
              <div className="empty-state">
                <h3>🎉 No resources expiring in this timeframe!</h3>
                <p>All allocations are stable for the selected period.</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Chart View - Charts Only */
        <>
          {/* Expiring Resources Summary Chart */}
          <div className="chart-section">
            <h2>Expiring Resources by Timeframe</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                data={[
                  { period: 'Next 10 Days', count: expiringResources?.next10days?.length || 0, fill: EXPIRY_COLORS.next10days },
                  { period: 'Next 20 Days', count: expiringResources?.next20days?.length || 0, fill: EXPIRY_COLORS.next20days },
                  { period: 'Next 30 Days', count: expiringResources?.next30days?.length || 0, fill: EXPIRY_COLORS.next30days }
                ]}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Expiring Resources by Project */}
          <div className="chart-section">
            <h2>Expiring Resources by Project</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={Object.entries(
                    getExpiryData().reduce((acc, resource) => {
                      const employeeId = findEmployeeId(resource);
                      const projectName = findProjectNameByEmployeeId(employeeId, resource.project);
                      acc[projectName] = (acc[projectName] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([project, count]) => ({ name: project, value: count }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {Object.entries(
                    getExpiryData().reduce((acc, resource) => {
                      const employeeId = findEmployeeId(resource);
                      const projectName = findProjectNameByEmployeeId(employeeId, resource.project);
                      acc[projectName] = (acc[projectName] || 0) + 1;
                      return acc;
                    }, {})
                  ).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={Object.values(COLORS)[index % Object.values(COLORS).length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* Project Allocations Detail View Modal */}
      <DetailView
        viewDetails={viewProjectDetails}
        setViewDetails={handleCloseProjectDetailModal}
        selectedEmpId={selectedEmployeeIdForDetail}
        selectedPeriodWeek={null}
        headingName={Modules.PROJECT_MANAGEMENT}
        selectedProjectAllocationId={null}
        selectedProjectId={null}
      />
    </div>
  );
};

export default ExpiringView; 